import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { io } from 'socket.io-client';
import { QUERY_CLIENT } from '@services/query.service';

import './monitor-layout.css';

const socket = io({ autoConnect: false });
const isDevelopment = import.meta.env.VITE_NAZKA_ENV === 'development';
const WAITING_TRANSITION_DELAY = 600;

function MonitorLayout() {
  const [tabletRoutes, setTabletRoutes] = useState({});
  const [tabletLanguages, setTabletLanguages] = useState({});
  const [lastAnswers, setLastAnswers] = useState({});
  const waitingTimers = useRef(new Map());

  useEffect(() => {
    const timers = waitingTimers.current;

    function handleConnect() {
      socket.emit('register', 'monitor');
    }

    function handleTabletRoute({ tabletId, route, lang }) {
      const clearWaitingTimer = () => {
        const timer = timers.get(tabletId);

        if (!timer) return;

        clearTimeout(timer);
        timers.delete(tabletId);
      };

      if (route === 'waiting') {
        clearWaitingTimer();
        const timer = setTimeout(() => {
          timers.delete(tabletId);
          setTabletRoutes(currentRoutes => ({
            ...currentRoutes,
            [tabletId]: 'waiting',
          }));
          setTabletLanguages((currentLanguages) => {
            const nextLanguages = { ...currentLanguages };
            delete nextLanguages[tabletId];
            return nextLanguages;
          });
          setLastAnswers((currentAnswers) => {
            const nextAnswers = { ...currentAnswers };
            delete nextAnswers[tabletId];
            return nextAnswers;
          });
        }, WAITING_TRANSITION_DELAY);

        timers.set(tabletId, timer);
        return;
      }

      clearWaitingTimer();
      setTabletRoutes(currentRoutes => ({
        ...currentRoutes,
        [tabletId]: route,
      }));
      setTabletLanguages(currentLanguages => ({
        ...currentLanguages,
        [tabletId]: lang || 'nl',
      }));
    }

    function handleNewAnswer(questionId, answer, tabletId, participantId, lang) {
      QUERY_CLIENT.invalidateQueries({ queryKey: ['answers'] });
      QUERY_CLIENT.invalidateQueries({ queryKey: ['participant-answers'] });
      setTabletLanguages(currentLanguages => ({
        ...currentLanguages,
        [tabletId]: lang || 'nl',
      }));
      setLastAnswers(currentAnswers => ({
        ...currentAnswers,
        [tabletId]: {
          questionId,
          answer,
          lang: lang || 'nl',
          participantId,
          tabletId,
        },
      }));
    }

    socket.on('connect', handleConnect);
    socket.on('tablet_route', handleTabletRoute);
    socket.on('new_answer', handleNewAnswer);
    socket.connect();

    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
      socket.off('connect', handleConnect);
      socket.off('tablet_route', handleTabletRoute);
      socket.off('new_answer', handleNewAnswer);
      socket.disconnect();
    };
  }, []);

  return (
    <div className={`monitor-layout ${isDevelopment ? 'monitor-layout--development-preview' : ''}`.trim()}>
      <div className="monitor-layout__stage">
        <Outlet
          context={{
            socket,
            tabletRoutes,
            tabletLanguages,
            lastAnswers,
          }}
        />
      </div>
    </div>
  );
}

export default MonitorLayout;